import React, {
  createContext,
  useContext,
  useRef,
  SyntheticEvent,
  ChangeEvent,
  useCallback,
  forwardRef,
} from "react";
import {
  InputProps,
  FieldTitle,
  ResettableTextField,
  useLocale,
  useTranslate,
} from "react-admin";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import { makeStyles } from "@material-ui/core";
import {
  dateFnsLocaleMap,
  ProLogsLocale,
} from "../../../common/util/localization";
import { enUS } from "date-fns/locale";
import { addMinutes } from "date-fns";
import { differenceInMinutes } from "date-fns/esm";
import ProLogsDateFnsUtils from "../../../common/datetime/ProLogsDateFnsUtils";

interface Props extends InputProps {
  PickerComponent: React.FC<any>;
  options?: any;
  timeZone?: string | null;
  value?: Date;
}

interface IInputProps extends InputProps {
  id?: string;
  onChange: (event: any) => void;
}

const InputPropsContext = createContext<IInputProps>({
  source: "",
  id: "",
  onChange: () => {},
});

const useStyles = makeStyles(
  (theme) => ({
    input: {
      minWidth: theme.spacing(20),
    },
  }),
  { name: "RaPickerInput" }
);

const ResettableTextFieldWithProps = ({
  defaultValue,
  onClick,
  ...props
}: any) => {
  const classes = useStyles();
  const context = useContext(InputPropsContext);
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <ResettableTextField
      inputRef={inputRef}
      source={context.source}
      id={context.id}
      className={classes.input}
      onChange={(e: SyntheticEvent<ChangeEvent>) => {
        context.onChange(e);
      }}
      onClick={(e: SyntheticEvent<MouseEvent>) => {
        if (e.target === inputRef.current && onClick) {
          onClick(e);
        }
      }}
      {...props}
      resettable
    />
  );
};

export const GenericCompactDateOrTimePicker = forwardRef<unknown, Props>(
  (
    { PickerComponent, className, options, disabled, timeZone, ...props },
    ref
  ) => {
    const { variant, onChange } = props;
    const timeZoneDifference = useRef<number>(0);
    const handleChange = useCallback(
      (value) => {
        const dateTime = Date.parse(value);
        if (!onChange) {
          return;
        }

        if (dateTime) {
          onChange(
            addMinutes(dateTime, timeZoneDifference.current).toISOString()
          );
          return;
        }

        onChange(null);
      },
      [onChange]
    );

    const formatValue = useCallback(
      (value) => {
        const dateTimeString = value;
        if (dateTimeString) {
          const originalDate = new Date(dateTimeString);
          if (!timeZone) {
            return originalDate;
          }

          const parsedDate = new Date(
            Date.parse(originalDate.toLocaleString("en-US", { timeZone }))
          );
          timeZoneDifference.current = differenceInMinutes(
            originalDate,
            parsedDate
          );

          return parsedDate;
        }

        return null;
      },
      [timeZone]
    );

    const translate = useTranslate();
    const locale = useLocale() as ProLogsLocale;
    return (
      <MuiPickersUtilsProvider
        utils={ProLogsDateFnsUtils}
        locale={!!dateFnsLocaleMap[locale] ? dateFnsLocaleMap[locale] : enUS}
      >
        <InputPropsContext.Provider
          value={{ source: props.source, onChange: onChange ?? (() => {}) }}
        >
          <PickerComponent
            ref={ref}
            disabled={disabled}
            className={className}
            label={
              props.label !== "" &&
              props.label !== false && (
                <FieldTitle
                  label={props.label}
                  source={props.source}
                  resource={props.resource}
                  isRequired={props.isRequired}
                />
              )
            }
            onChange={(value: any) => handleChange(value)}
            inputVariant={variant}
            TextFieldComponent={ResettableTextFieldWithProps}
            {...options}
            okLabel={translate("ra.action.confirm")}
            cancelLabel={translate("ra.action.cancel")}
            value={formatValue(props.value)}
            {...props}
          />
        </InputPropsContext.Provider>
      </MuiPickersUtilsProvider>
    );
  }
);
